/**
 * Auth store.
 *
 * Phase 3.1 (cookie-refresh cutover) ownership rules:
 *   - Access token is **memory only**. It never reaches the persisted
 *     storage layer; a full page reload always re-bootstraps via the
 *     HttpOnly refresh cookie.
 *   - Refresh token is **never** read or stored client-side. The cookie
 *     belongs to the browser/backend; the dashboard cannot see it.
 *   - `user` is persisted only as a UX hint (avoids guest-route flicker
 *     during bootstrap). It is NOT proof of authentication — protected
 *     routes wait for `SessionBootstrap` to confirm the cookie is valid.
 *   - `isAuthenticated` is a derived selector (`!!accessToken`). A
 *     persisted boolean would lie until bootstrap finishes; a derived
 *     value tracks the actual in-memory token.
 *
 * Migration:
 *   v0 (pre-3.1) persisted `{ user, accessToken, refreshToken,
 *   isAuthenticated }`. The v0→v1 migrator strips token + boolean keys
 *   so a previously-deployed user upgrading mid-session does NOT carry
 *   stale tokens forward.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  phoneVerifiedAt?: string | null;
  email?: string | null;
  businessId?: string | null;
  hasRegisteredBusiness: boolean;
  roles: string[];
  permissions: string[];
}

export interface AuthState {
  user: User | null;
  accessToken: string | null;

  setAuth: (user: User, accessToken: string) => void;
  setAccessToken: (accessToken: string) => void;
  setUser: (user: User) => void;
  logout: () => void;
  isAuthenticated: () => boolean;
  hasPermission: (permission: string) => boolean;
}

const STORAGE_KEY = 'invento-auth';
const STORAGE_VERSION = 1;

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,

      setAuth: (user, accessToken) => set({ user, accessToken }),

      setAccessToken: (accessToken) => set({ accessToken }),

      setUser: (user) => set({ user }),

      logout: () =>
        set({
          user: null,
          accessToken: null,
        }),

      isAuthenticated: () => !!get().accessToken,

      hasPermission: (permission) => {
        const { user } = get();
        return user?.permissions.includes(permission) ?? false;
      },
    }),
    {
      name: STORAGE_KEY,
      version: STORAGE_VERSION,
      // Only `user` reaches localStorage. Tokens stay in memory.
      partialize: (state) => ({ user: state.user }),
      // Strip any pre-3.1 persisted token / isAuthenticated keys so a
      // user upgrading mid-session does not keep secrets in storage.
      migrate: (persisted, _from) => {
        if (!persisted || typeof persisted !== 'object') return { user: null };
        const { user } = persisted as { user?: User | null };
        return { user: user ?? null };
      },
    },
  ),
);
